import { Button, Form, Select, InputNumber, DatePicker, Radio } from 'antd'
import { memo, useCallback, useEffect, useState } from 'react'
import { Game } from 'types/games'
import { TeamToLeague } from 'types/leagues'
import { getTeamsToLeague } from 'services/teams_to_leagues'
import dayjs from 'dayjs'
import { getStages } from 'services/stage'
import { Stage } from 'types/stage'

type Props = {
    game: Game
    onSubmit: (game: Game) => void
    loading: boolean
    leagueId: number
}

export const GameForm = memo((props: Props) => {
    const { game, onSubmit, loading, leagueId } = props
    const [teamsToLeague, setTeamsToLeague] = useState<TeamToLeague[]>([])
    const [stages, setStages] = useState<Stage[]>([])

    const [typeIsRegular, setTypeIsRegular] = useState<boolean>(game.regular ?? true)

    const fetchTeams = useCallback(() => {
        getTeamsToLeague(leagueId).then(setTeamsToLeague)
    }, [leagueId])

    const fetchStages = useCallback(() => {
        getStages().then(setStages)
    }, [])

    useEffect(() => {
        fetchTeams()
        fetchStages()
    }, [fetchStages, fetchTeams])

    const onFinish = useCallback(
        (values: Game) => {
            const payload = { ...values }
            if (payload.gameTime) {
                payload.gameTime = Math.floor(new Date(payload.gameTime).getTime() / 1000)
            } else {
                payload.gameTime = null
            }
            onSubmit(payload)
        },
        [onSubmit]
    )

    const onValuesChange = useCallback((changedValues: Game, allValues: Game) => {
        if (changedValues.regular !== undefined) {
            setTypeIsRegular(Number(changedValues.regular) === 1)
        }
    }, [])

    return (
        <Form
            name='basic'
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onValuesChange={onValuesChange}
            autoComplete='off'
            disabled={loading}
        >
            <Form.Item label={'Match type'} name={'regular'} initialValue={game.regular ?? true}>
                <Radio.Group>
                    <Radio.Button value={true}>Regular</Radio.Button>
                    <Radio.Button value={false}>Play-off/Cup</Radio.Button>
                </Radio.Group>
            </Form.Item>
            {typeIsRegular ? (
                <Form.Item initialValue={game.round} label='Round' name='round'>
                    <InputNumber />
                </Form.Item>
            ) : null}
            {!typeIsRegular ? (
                <Form.Item initialValue={game.stageId} name='stageId' label='Stage'>
                    <Select>
                        {stages.map(it => (
                            <Select.Option key={it.id} value={it.id}>
                                {it.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            ) : null}
            <Form.Item
                initialValue={game.teamHomeId}
                name='teamHomeId'
                label='Home team'
                rules={[{ required: true, message: 'Please select home team!' }]}
            >
                <Select>
                    {teamsToLeague.map(it => (
                        <Select.Option key={it.teamId} value={it.teamId}>
                            {it.team.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item initialValue={game.homeScore} label='Home score' name='homeScore'>
                <InputNumber />
            </Form.Item>
            {!typeIsRegular ? (
                <>
                    <Form.Item initialValue={game.homePen} label='Home pen' name='homePen'>
                        <InputNumber />
                    </Form.Item>
                    <Form.Item initialValue={game.awayPen} label='Away pen' name='awayPen'>
                        <InputNumber />
                    </Form.Item>
                </>
            ) : null}

            <Form.Item initialValue={game.awayScore} label='Away score' name='awayScore'>
                <InputNumber />
            </Form.Item>
            <Form.Item
                initialValue={game.teamAwayId}
                name='teamAwayId'
                label='Away team'
                rules={[{ required: true, message: 'Please select away team!' }]}
            >
                <Select>
                    {teamsToLeague.map(it => (
                        <Select.Option key={it.teamId} value={it.teamId}>
                            {it.team.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                initialValue={game.gameTime && dayjs(new Date(game.gameTime * 1000))}
                label='Game time'
                name='gameTime'
            >
                <DatePicker
                    allowClear
                    minuteStep={5}
                    format='DD.MM.YYYY HH:mm'
                    showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm:ss') }}
                />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                <Button type='primary' htmlType='submit'>
                    Submit
                </Button>
                <Button htmlType={'reset'}>Reset</Button>
            </Form.Item>
        </Form>
    )
})
